import {Signal} from '@angular/core';
import {ActivatedRouteSnapshot, Data} from '@angular/router';

/**
 * Top-level application route configuration interface.
 */
export interface AppRoute {
  readonly app: string;
  readonly path: string;
  readonly title: string;
  /** Short title used for page layouts. */
  readonly pageLayoutTitle?: string;
  /** If provided, the app route is only visible when the flag value is true. */
  readonly enablementFeatureFlag?: string;
}

/**
 * Known top-level application route keys.
 */
type AppRouteKey =
  | 'LOGIN'
  | 'ACCESS_DENIED'
  | 'GPT'
  | 'GPTD'
  | 'HYPERQUEUE'
  | 'GRIDOPS'
  | 'SIMULATIONS'
  | 'HOSTING_CAPACITY'
  | 'MODEL_BUILDER';

/**
 * Top-level application routes and optional page titles.
 *
 * Note: This file is strictly for global, top-level application roots
 * (e.g. /gpt, /simulations). Internal app routes should be maintained
 * within their respective feature modules (e.g. hosting_capacity/routing.ts).
 */
export const AppRoutes = {
  LOGIN: {app: 'login', path: '/login', title: 'Tapestry | Login'},
  ACCESS_DENIED: {
    app: 'access-denied',
    path: '/access-denied',
    title: 'Tapestry | Access Denied',
  },
  GPT: {
    app: 'gpt',
    path: '/gpt',
    title: 'Grid Planning Tool - Transmission',
    pageLayoutTitle: 'GPT-T',
  },
  GPTD: {
    app: 'gptd',
    path: '/gptd',
    title: 'Grid Planning Tool - Distribution',
    pageLayoutTitle: 'GPT-D',
  },
  HYPERQUEUE: {
    app: 'hyperqueue',
    path: '/hyperqueue',
    title: 'Interconnection Queue Analysis Tool',
    pageLayoutTitle: 'HyperQ',
  },
  GRIDOPS: {
    app: 'gridops',
    path: '/gridops',
    title: 'Grid Operations Tool',
    pageLayoutTitle: 'GridOps',
  },
  SIMULATIONS: {
    app: 'simulations',
    path: '/simulations',
    title: 'Simulations',
    pageLayoutTitle: 'Simulations',
  },
  HOSTING_CAPACITY: {
    app: 'hosting-capacity',
    path: '/hosting-capacity',
    title: 'Hosting Capacity Tool',
    pageLayoutTitle: 'Hosting Capacity Tool',
  },
  MODEL_BUILDER: {
    app: 'model-weaver',
    path: '/model-weaver',
    title: 'Model Weaver',
    pageLayoutTitle: 'Model Weaver',
    enablementFeatureFlag: 'umm-model-builder',
  },
} as const satisfies Record<AppRouteKey, AppRoute>;

/**
 * A union of all valid top-level application identifiers (e.g. 'login' | 'gpt' | ...).
 * Automatically derived from the `AppRoutes` configuration.
 */
export type AppId = (typeof AppRoutes)[AppRouteKey]['app'];

/**
 * A union of all valid top-level application paths (e.g. '/login' | '/gpt' | ...).
 * Automatically derived from the `AppRoutes` configuration.
 */
export type RootAppUrl = (typeof AppRoutes)[AppRouteKey]['path'];

/** Metadata fields configured in Angular Router's `Route.data` object. */
export interface RouteData extends Data {
  /** Top-level application identifier (e.g., GPT, GridOps). */
  appPathIdentifier?: AppId;

  /** Absolute URL path used for redirection and feature flag fallbacks. */
  appPath?: string;

  /** Application title displayed in the global header or navigation bar. */
  appTitle?: string;

  /** Title displayed in the page layout component (`tap-page-layout`). */
  pageLayoutTitle?: string;

  /** Breadcrumb configuration for `AppRouteDataService`. */
  breadcrumb?: BreadcrumbDefinition;
}

/** Callback function returning a static string or reactive signal for a route breadcrumb. */
export type BreadcrumbCallback = (route: ActivatedRouteSnapshot) => string | Signal<string>;

/** Breadcrumb definition as either a static label or a resolver callback. */
export type BreadcrumbDefinition = string | BreadcrumbCallback;
